import { LatLng } from '../../../utils/types';

// Steps in the could-buy flow: 'main' is the merged single-screen form (Figma 3592:90002);
// 'map' is the full-screen area picker; 'success' is the post-submit screen.
export type CouldBuyStep = 'main' | 'map' | 'success';

// A GeoJSON Polygon/MultiPolygon as returned by the area resolution service (JAA-276).
export interface AreaGeometry {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: number[][][] | number[][][][];
}

export type AreaLevel = 'sub-area' | 'district' | 'city';

// One area the buyer picked: the fixed 500 m circle around the anchor building,
// or a named admin area (district/city). Matches the JAA-277 `areas[]` POST
// contract: { kind, ext_id, name, level } — geometry is resolved server-side
// from ext_id and is NOT sent by the client.
export type AreaPick =
  | { kind: 'circle' }
  | {
      kind: 'area';
      // Stable external id from the resolution service.
      ext_id: string;
      name: string;
      level: AreaLevel;
    };

export type NamedArea = Extract<AreaPick, { kind: 'area' }>;

// An in-view area as returned by GET /neighborhoods/in-view (JAA-276): a NamedArea
// pick enriched with the polygon geometry and bbox the map picker needs to draw
// and label it. The geometry is picker-internal only — it is NOT part of the POST
// `areas[]` contract (the backend re-resolves geometry from ext_id at store time),
// so MapPickerStep strips it back to a NamedArea before handing picks to the form.
export interface InViewArea extends NamedArea {
  geometry: AreaGeometry;
  bbox: [number, number, number, number];
}

// The building the buyer tapped "Buy here" on. The flow opens seeded from it:
// the 500 m circle centres on it and its district is prefilled.
export interface CouldBuySeed {
  buildingId: string;
  coordinates: LatLng | null;
  address: { street: string; houseNumber: string; city: string; country: string };
}

export interface CouldBuyFormState {
  // Named areas the buyer added (deduped by ext_id, falling back to lowercased name).
  areas: NamedArea[];
  // The fixed 500 m circle around the anchor building, on/off.
  circleSelected: boolean;
  // Where that circle is centred. Sent with the circle so the interest carries
  // its own geometry (JAA-328); null only until a building has been picked.
  circleCentre: LatLng | null;
  // Interest in the seed/anchor building itself (the "Lönnrotinkatu 19" chip),
  // on/off. Default on. Shared with the map picker so its chip mirrors the form.
  anchorSelected: boolean;
  // Criteria (all default "Any" => omitted from the payload).
  sizeMin: string;
  sizeMax: string;
  roomsMin: string;
  // "Flexible with +/- 1 room" toggle on the Rooms section.
  roomsFlexible: boolean;
  priceMin: string;
  priceMax: string;
  // "Include unpriced listings" toggle on the Price section.
  // Floor preference: '' (any) | 'highest' | 'mid' | 'lowest'.
  floor: string;
  // Renovation status: '' (any) | 'recently_renovated' | 'good' | 'needs_renovation'.
  renovation: string;
  // Selected amenity keys, e.g. ['elevator', 'balcony'].
  amenities: string[];
  description: string;
  // Notify toggles. Figma defaults: email on, SMS off.
  notifyEmail: boolean;
  notifySms: boolean;
}

export const createInitialFormState = (): CouldBuyFormState => ({
  areas: [],
  circleSelected: false,
  circleCentre: null,
  anchorSelected: true,
  sizeMin: '',
  sizeMax: '',
  roomsMin: '',
  roomsFlexible: false,
  priceMin: '',
  priceMax: '',
  floor: '',
  renovation: '',
  amenities: [],
  description: '',
  notifyEmail: true,
  notifySms: false,
});

// v1 circle is a single fixed 500 m around the anchor, not a slider.
export const CIRCLE_RADIUS_M = 500;

export const MAX_PROPERTY_DESCRIPTION_LENGTH = 1100;
